import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LandingNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavLink = (id) => {
    if (id === 'community') {
      navigate('/community-hub');
      return;
    }
    scrollTo(id);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0B1F3B] shadow-xl' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => scrollTo('hero')} className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
            <img
              src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
              alt="WIN gold coin logo"
              className="w-full h-full object-cover"
              style={{ animation: 'spin 8s linear infinite' }}
            />
          </div>
          <span className="text-[#D4A520] font-bold text-xl tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}>WIN</span>
        </button>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          {[['features', 'Features'], ['membership', 'Membership'], ['academy', 'Academy'], ['community', 'Community']]?.map(([id, label]) => (
            <button
              key={id}
              onClick={() => handleNavLink(id)}
              className="text-white/80 hover:text-[#D4A520] text-sm font-medium transition-colors duration-200 tracking-wide"
            >
              {label}
            </button>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => navigate('/onboarding-authentication')}
            className="relative px-4 py-2 text-sm font-medium rounded-lg overflow-hidden"
            style={{
              border: '1px solid #D4A520',
              color: '#D4A520',
              background: 'transparent',
              transition: 'transform 250ms ease-out',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              const fill = e?.currentTarget?.querySelector('.nav-fill');
              if (fill) { fill.style.transform = 'translateX(0)'; }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              const fill = e?.currentTarget?.querySelector('.nav-fill');
              if (fill) { fill.style.transform = 'translateX(-100%)'; }
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
          >
            <span className="nav-fill absolute inset-0 pointer-events-none rounded-lg" style={{ background: '#0B1F3B', transform: 'translateX(-100%)', transition: 'transform 300ms ease-out' }} />
            <span className="relative z-10">Sign In</span>
          </button>
          <button
            onClick={() => navigate('/onboarding-authentication')}
            className="relative px-5 py-2 text-sm font-bold rounded-lg overflow-hidden"
            style={{
              background: '#D4A520',
              color: '#0B1F3B',
              transition: 'transform 250ms ease-out, box-shadow 250ms ease-out, background 250ms ease-out',
              boxShadow: '0 2px 8px rgba(212,165,32,0.2)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.04)';
              e.currentTarget.style.background = '#E8C040';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,165,32,0.5)';
              const shine = e?.currentTarget?.querySelector('.nav-shine');
              if (shine) { shine.style.transform = 'translateX(100%)'; shine.style.transition = 'transform 500ms ease'; }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = '#D4A520';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(212,165,32,0.2)';
              const shine = e?.currentTarget?.querySelector('.nav-shine');
              if (shine) { shine.style.transform = 'translateX(-100%)'; shine.style.transition = 'none'; }
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
          >
            <span className="nav-shine absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)', transform: 'translateX(-100%)' }} />
            <span className="relative z-10">Join Now</span>
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-[#D4A520] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 bg-[#D4A520] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 bg-[#D4A520] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>
      {/* Mobile Drawer */}
      <div className={`md:hidden bg-[#0B1F3B] border-t border-[#D4A520]/20 overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-4 py-4 flex flex-col gap-4">
          {[['features', 'Features'], ['membership', 'Membership'], ['academy', 'Academy'], ['community', 'Community']]?.map(([id, label]) => (
            <button key={id} onClick={() => handleNavLink(id)} className="text-white/80 hover:text-[#D4A520] text-left text-sm font-medium transition-colors py-1">{label}</button>
          ))}
          <div className="flex gap-3 pt-2 border-t border-[#D4A520]/20">
            <button onClick={() => navigate('/onboarding-authentication')} className="flex-1 py-2.5 border border-[#D4A520] text-[#D4A520] text-sm rounded-lg">Sign In</button>
            <button onClick={() => navigate('/onboarding-authentication')} className="flex-1 py-2.5 bg-[#D4A520] text-[#0B1F3B] text-sm font-bold rounded-lg">Join Now</button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default LandingNav;
